import { useEffect, useState } from "react";
import { Check, X, Flag, FileAudio, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Loading } from "../components/Layout";
import { Button } from "../components/ui/button";
import CoverImage from "../components/CoverImage";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Moderation() {
  const [queue, setQueue] = useState(null);
  const [reports, setReports] = useState([]);

  const load = async () => {
    const [q, r] = await Promise.all([api.get("/moderation/queue"), api.get("/moderation/reports")]);
    setQueue(q.data);
    setReports(r.data);
  };
  useEffect(() => { load(); }, []);

  const reviewStory = async (id, action) => {
    try {
      await api.post(`/moderation/stories/${id}/review`, { action, note: "" });
      toast.success(`Story ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reviewEpisode = async (id, action) => {
    try {
      await api.post(`/moderation/episodes/${id}/review`, { action, note: "" });
      toast.success(`Episode ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const resolveReport = async (id, action) => {
    try {
      await api.post(`/moderation/reports/${id}/resolve`, { action, note: "" });
      toast.success("Report resolved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (queue === null) return <Loading />;
  const pendingCount = queue.episodes.length + queue.stories.length;
  const openReports = reports.filter((r) => r.status === "open");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 animate-fade-up">
      <h1 className="font-display text-4xl mb-8">Moderation</h1>
      <Tabs defaultValue="queue">
        <TabsList className="bg-card/50 border border-white/10">
          <TabsTrigger value="queue" data-testid="tab-queue">Review Queue ({pendingCount})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports ({openReports.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-6 space-y-6">
          {pendingCount === 0 && <p className="text-muted-foreground py-16 text-center">Nothing awaiting review. Great work.</p>}

          {queue.stories.length > 0 && (
            <div>
              <h2 className="uppercase tracking-[0.2em] text-xs text-muted-foreground mb-3 flex items-center gap-2"><BookOpen size={14} /> Stories</h2>
              <div className="space-y-2" data-testid="mod-stories">
                {queue.stories.map((s) => (
                  <div key={s.id} className="flex items-center gap-4 border border-white/10 rounded-lg p-4 bg-card/40">
                    <div className="h-12 w-12 rounded overflow-hidden bg-secondary shrink-0">
                      {s.cover_url && (
                        <CoverImage
                          coverUrl={s.cover_url}
                          alt=""
                          data-testid={`moderation-cover-${s.id}`}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1"><p className="font-medium">{s.title}</p><p className="text-xs text-muted-foreground">{s.category}</p></div>
                    <Button data-testid={`approve-story-${s.id}`} size="sm" onClick={() => reviewStory(s.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700"><Check size={15} /></Button>
                    <Button data-testid={`reject-story-${s.id}`} size="sm" variant="outline" onClick={() => reviewStory(s.id, "reject")} className="border-primary/40 text-primary hover:bg-primary/10"><X size={15} /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {queue.episodes.length > 0 && (
            <div>
              <h2 className="uppercase tracking-[0.2em] text-xs text-muted-foreground mb-3 flex items-center gap-2"><FileAudio size={14} /> Episodes</h2>
              <div className="space-y-2" data-testid="mod-episodes">
                {queue.episodes.map((e) => (
                  <div key={e.id} className="flex items-center gap-4 border border-white/10 rounded-lg p-4 bg-card/40">
                    <div className="flex-1">
                      <p className="font-medium">{e.title}</p>
                      <p className="text-xs text-muted-foreground">{e.story_title} · by {e.creator_name} · {e.has_audio ? "audio ✓" : "no audio"}</p>
                    </div>
                    <Button data-testid={`approve-episode-${e.id}`} size="sm" onClick={() => reviewEpisode(e.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700"><Check size={15} /></Button>
                    <Button data-testid={`reject-episode-${e.id}`} size="sm" variant="outline" onClick={() => reviewEpisode(e.id, "reject")} className="border-primary/40 text-primary hover:bg-primary/10"><X size={15} /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <div className="space-y-2" data-testid="reports-list">
            {reports.length === 0 && <p className="text-muted-foreground py-16 text-center">No reports.</p>}
            {reports.map((r) => (
              <div key={r.id} className="flex items-center gap-4 border border-white/10 rounded-lg p-4 bg-card/40">
                <Flag size={16} className="text-primary shrink-0" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="text-sm"><span className="capitalize">{r.target_type}</span> reported by {r.reporter_name}</p>
                  <p className="text-xs text-muted-foreground">{r.reason}</p>
                </div>
                {r.status === "open" ? (
                  <>
                    <Button data-testid={`report-action-${r.id}`} size="sm" onClick={() => resolveReport(r.id, "actioned")} className="bg-primary hover:bg-primary/90">Action</Button>
                    <Button data-testid={`report-dismiss-${r.id}`} size="sm" variant="outline" onClick={() => resolveReport(r.id, "dismiss")} className="border-white/10 hover:bg-white/5">Dismiss</Button>
                  </>
                ) : (
                  <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{r.status}</span>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
